import { Injectable } from "@angular/core";
import { firstValueFrom, Observable, ReplaySubject } from "rxjs";
import { PlatformService } from "../platform/platform.service";
import { GlobalConstants } from "@src/app/global-constants";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";

@Injectable()
export class CookieService {
  // Constantes globales críticas
  private readonly API_URL: string;
  private readonly constantesGlobales: any;
  cookiesPrensa$: ReplaySubject<any> = new ReplaySubject(1);

  constructor(
    private readonly httpClient: HttpClient,
    private readonly platformService: PlatformService,
  ) {
    const host = this.platformService.getLocationHostname();
    this.constantesGlobales = new GlobalConstants(host);
    this.API_URL = this.constantesGlobales.urlApi;
  }

  /**
   * Obtiene las cookies de la API de prensa para un ámbito específico.
   *
   * @param idAmbito - El identificador del ámbito para el cual se desean obtener las cookies.
   * @returns Un observable que emite la respuesta de la API o un error en caso de fallo.
   *
   * @remarks
   * Este método realiza una solicitud HTTP GET a la API especificada en `this.API_URL`
   * con los encabezados y parámetros necesarios. La respuesta se emite a través de un Observable.
   *
   */
  public getCookiesPrensaApi(idAmbito: number): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    const body = new HttpParams().set("id_ambito", idAmbito);

    return new Observable((observer) => {
      this.httpClient.get(this.API_URL + "/getCookiesPrensa", { headers: headers, params: body }).subscribe({
        next: (response) => {
          observer.next(response);
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        },
      });
    });
  }

  /**
   * Carga las cookies de prensa desde la API y actualiza el observable `cookiesPrensa$`.
   *
   * @returns {Promise<any>} Una promesa que se resuelve con las cookies obtenidas o se rechaza con un error.
   *
   * @description
   * Este método utiliza el servicio `noticiasService` para obtener las cookies de prensa
   * asociadas al ámbito actual (`idAmbito`). Si la API devuelve datos, se procesan y se
   * actualiza el observable `cookiesPrensa$` con las cookies obtenidas. En caso de error,
   * la promesa se rechaza con el error correspondiente.
   */
  loadCookiesPrensa() {
    return new Promise((resolve, reject) => {
      const cookiesPrensaApi = this.getCookiesPrensaApi(this.constantesGlobales.idAmbito);
      if (cookiesPrensaApi) {
        firstValueFrom(cookiesPrensaApi)
          .then((cookies) => {
            this.cookiesPrensa$.next(cookies);
            resolve(cookies);
          })
          .catch((error) => reject(error));
      }
    });
  }
}
